import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Bell,
  CheckCircle2,
  PlayCircle,
  Plus,
  RefreshCcw,
  ShieldAlert,
  Trash2,
  XCircle,
} from "lucide-react";
import {
  createRule,
  deleteRule,
  evaluateRule,
  listRecentAlerts,
  listRules,
  type AnomalyAlert,
  type AnomalyRule,
} from "@/lib/api";

/**
 * CRUD + dry-run for AnomalyRule. The form below the table is the
 * "create new rule" panel; existing rules render inline with an
 * "evaluate now" trigger that fires the rule once and shows whether
 * it breached.
 */
export function RulesPage() {
  const qc = useQueryClient();
  const { data, isLoading, error, refetch } = useQuery<AnomalyRule[]>({
    queryKey: ["anomaly-rules"],
    queryFn: listRules,
  });
  const { data: alerts } = useQuery<AnomalyAlert[]>({
    queryKey: ["anomaly-alerts", "recent"],
    queryFn: listRecentAlerts,
    refetchInterval: 30_000,
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteRule(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["anomaly-rules"] }),
  });
  const evaluate = useMutation({
    mutationFn: (id: string) => evaluateRule(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["anomaly-rules"] });
      qc.invalidateQueries({ queryKey: ["anomaly-alerts", "recent"] });
    },
  });

  return (
    <div className="mx-auto max-w-5xl px-6 py-8">
      <header className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold text-datumcraft-navy">
            Anomaly rules
          </h1>
          <p className="mt-0.5 text-sm text-datumcraft-slate">
            SQL-backed thresholds. The scheduler evaluates active rules every minute and
            records breaches. Webhook delivery posts a JSON payload; email delivery is
            staged until SMTP lands.
          </p>
        </div>
        <button
          onClick={() => refetch()}
          className="inline-flex items-center gap-1 rounded-md border border-datumcraft-slate/30 bg-white px-3 py-1 text-xs font-medium text-datumcraft-ink hover:bg-datumcraft-mist"
        >
          <RefreshCcw size={11} />
          Refresh
        </button>
      </header>

      {isLoading && <p className="text-sm text-datumcraft-slate">Loading…</p>}
      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700">
          Failed to load — {(error as Error).message}
        </p>
      )}

      <div data-tour-id="rules-create">
        <CreateRuleForm onCreated={() => qc.invalidateQueries({ queryKey: ["anomaly-rules"] })} />
      </div>

      {data && data.length > 0 && (
        <ul data-tour-id="rules-list" className="mt-6 grid gap-2">
          {data.map((r) => (
            <RuleRow
              key={r.id}
              rule={r}
              onEvaluate={() => evaluate.mutate(r.id)}
              onDelete={() => {
                if (confirm(`Delete rule "${r.name}"?`)) remove.mutate(r.id);
              }}
              isEvaluating={evaluate.isPending && evaluate.variables === r.id}
            />
          ))}
        </ul>
      )}

      {alerts && alerts.length > 0 && (
        <section className="mt-8">
          <h2 className="font-display text-base font-semibold text-datumcraft-navy">
            Recent breaches
          </h2>
          <ul className="mt-2 grid gap-1.5">
            {alerts.slice(0, 20).map((a) => (
              <AlertRow key={a.id} alert={a} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function RuleRow({
  rule,
  onEvaluate,
  onDelete,
  isEvaluating,
}: {
  rule: AnomalyRule;
  onEvaluate: () => void;
  onDelete: () => void;
  isEvaluating: boolean;
}) {
  const tone =
    rule.lastStatus === "BREACHED"
      ? "border-rose-200 bg-rose-50/40"
      : rule.lastStatus === "ERROR"
      ? "border-amber-200 bg-amber-50/40"
      : rule.lastStatus === "OK"
      ? "border-emerald-200 bg-emerald-50/40"
      : "border-datumcraft-slate/20 bg-white";

  const icon =
    rule.lastStatus === "BREACHED" ? <ShieldAlert size={14} className="text-rose-600" />
    : rule.lastStatus === "ERROR" ? <AlertTriangle size={14} className="text-amber-600" />
    : rule.lastStatus === "OK" ? <CheckCircle2 size={14} className="text-emerald-600" />
    : <Bell size={14} className="text-datumcraft-slate" />;

  return (
    <li className={`flex flex-col gap-2 rounded-md border p-3 ${tone}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-2">
          {icon}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-medium text-datumcraft-ink">{rule.name}</span>
              {!rule.active && (
                <span className="rounded-full border border-datumcraft-slate/30 bg-white px-1.5 py-0.5 text-2xs font-medium text-datumcraft-slate">
                  inactive
                </span>
              )}
            </div>
            {rule.description && <p className="text-2xs text-datumcraft-slate">{rule.description}</p>}
            <p className="mt-1 text-2xs font-mono text-datumcraft-slate">
              breach when result {rule.operator} {rule.threshold} ·{" "}
              <span className="text-datumcraft-steel">{rule.alertAction.toLowerCase()}</span>
              {rule.alertTarget && <> → {rule.alertTarget}</>}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button
            type="button"
            onClick={onEvaluate}
            disabled={isEvaluating}
            className="inline-flex items-center gap-1 rounded-md border border-datumcraft-slate/30 bg-white px-2 py-1 text-2xs font-medium text-datumcraft-ink hover:bg-datumcraft-mist disabled:opacity-60"
          >
            <PlayCircle size={11} />
            {isEvaluating ? "Running…" : "Evaluate"}
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-white px-2 py-1 text-2xs font-medium text-rose-700 hover:bg-rose-50"
          >
            <Trash2 size={11} />
          </button>
        </div>
      </div>
      <details className="text-xs text-datumcraft-slate">
        <summary className="cursor-pointer">Show SQL</summary>
        <pre className="mt-1 max-h-40 overflow-auto rounded bg-white px-2 py-1 text-[11px] text-datumcraft-ink">
          {rule.metricSql}
        </pre>
      </details>
    </li>
  );
}

function AlertRow({ alert }: { alert: AnomalyAlert }) {
  const tone =
    alert.deliveryStatus === "DELIVERED"
      ? "text-emerald-700"
      : alert.deliveryStatus === "FAILED"
      ? "text-rose-700"
      : alert.deliveryStatus === "SKIPPED"
      ? "text-amber-700"
      : "text-datumcraft-steel";
  return (
    <li className="flex items-center gap-2 rounded-md border border-datumcraft-slate/15 bg-white px-3 py-1.5 text-xs">
      <XCircle size={11} className="shrink-0 text-rose-500" />
      <span className="font-mono text-2xs text-datumcraft-slate">
        {new Date(alert.triggeredAt).toLocaleString()}
      </span>
      <span className="text-datumcraft-ink">
        observed {alert.observedValue} {alert.operatorAtTrigger} {alert.thresholdAtTrigger}
      </span>
      <span className={`ml-auto font-medium ${tone}`}>{alert.deliveryStatus.toLowerCase()}</span>
    </li>
  );
}

function CreateRuleForm({ onCreated }: { onCreated: () => void }) {
  const [name, setName] = useState("");
  const [metricSql, setMetricSql] = useState("");
  const [operator, setOperator] = useState<AnomalyRule["operator"]>(">");
  const [threshold, setThreshold] = useState("0");
  const [alertAction, setAlertAction] = useState<AnomalyRule["alertAction"]>("LOG");
  const [alertTarget, setAlertTarget] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () =>
      createRule({
        name: name.trim(),
        description: null,
        metricSql: metricSql.trim(),
        operator,
        threshold: Number(threshold),
        alertAction,
        alertTarget: alertTarget.trim() || null,
        active: true,
      }),
    onSuccess: () => {
      setName("");
      setMetricSql("");
      setThreshold("0");
      setAlertTarget("");
      setError(null);
      onCreated();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <section className="rounded-xl border border-datumcraft-slate/20 bg-white p-4">
      <h2 className="font-display text-base font-semibold text-datumcraft-navy">
        New rule
      </h2>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-xs">
          <span className="mb-1 block font-medium text-datumcraft-ink">Name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Daily failed runs > 50"
            className="w-full rounded-md border border-datumcraft-slate/30 px-2 py-1.5 text-sm"
          />
        </label>
        <div className="grid grid-cols-[100px,1fr] gap-2">
          <label className="text-xs">
            <span className="mb-1 block font-medium text-datumcraft-ink">Operator</span>
            <select
              value={operator}
              onChange={(e) => setOperator(e.target.value as AnomalyRule["operator"])}
              className="w-full rounded-md border border-datumcraft-slate/30 bg-white px-2 py-1.5 text-sm"
            >
              <option value=">">{">"}</option>
              <option value=">=">{">="}</option>
              <option value="<">{"<"}</option>
              <option value="<=">{"<="}</option>
              <option value="==">==</option>
              <option value="!=">!=</option>
            </select>
          </label>
          <label className="text-xs">
            <span className="mb-1 block font-medium text-datumcraft-ink">Threshold</span>
            <input
              type="number"
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              className="w-full rounded-md border border-datumcraft-slate/30 px-2 py-1.5 text-sm"
            />
          </label>
        </div>
      </div>

      <label className="mt-3 block text-xs">
        <span className="mb-1 block font-medium text-datumcraft-ink">
          Metric SQL (must return a single numeric column in the first row)
        </span>
        <textarea
          value={metricSql}
          onChange={(e) => setMetricSql(e.target.value)}
          rows={4}
          placeholder="select count(*) from cogipf_runreport where cogipf_completed_time >= dateadd(day, -1, current_timestamp)"
          className="w-full rounded-md border border-datumcraft-slate/30 px-2 py-1.5 font-mono text-xs"
        />
      </label>

      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <label className="text-xs">
          <span className="mb-1 block font-medium text-datumcraft-ink">Alert action</span>
          <select
            value={alertAction}
            onChange={(e) => setAlertAction(e.target.value as AnomalyRule["alertAction"])}
            className="w-full rounded-md border border-datumcraft-slate/30 bg-white px-2 py-1.5 text-sm"
          >
            <option value="LOG">Log only</option>
            <option value="WEBHOOK">Webhook</option>
            <option value="EMAIL">Email (not yet wired)</option>
          </select>
        </label>
        <label className="text-xs">
          <span className="mb-1 block font-medium text-datumcraft-ink">Target (URL or email)</span>
          <input
            value={alertTarget}
            onChange={(e) => setAlertTarget(e.target.value)}
            placeholder={alertAction === "WEBHOOK" ? "https://hooks.example.com/…" : "ops@example.com"}
            disabled={alertAction === "LOG"}
            className="w-full rounded-md border border-datumcraft-slate/30 px-2 py-1.5 text-sm disabled:bg-datumcraft-mist"
          />
        </label>
      </div>

      {error && (
        <p className="mt-3 rounded-md border border-rose-200 bg-rose-50 p-2 text-xs text-rose-700">
          {error}
        </p>
      )}

      <div className="mt-4">
        <button
          type="button"
          onClick={() => submit.mutate()}
          disabled={!name.trim() || !metricSql.trim() || submit.isPending}
          className="inline-flex items-center gap-1 rounded-md bg-datumcraft-navy px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-datumcraft-ink disabled:opacity-50"
        >
          <Plus size={12} />
          {submit.isPending ? "Creating…" : "Create rule"}
        </button>
      </div>
    </section>
  );
}
